import { CpuChipIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import Tag from 'components/common/tag';
import ProductLink from 'components/products/product-link';
import ProductPhoto from 'components/products/product-photo';
import {
  AirHandlerProductType,
  CondenserProductType,
  CondenserType,
  ValidSystemType
} from 'data/types';
import { useEffect, useState } from 'react';
import { abbreviateNumber, formatUSD } from 'utils/helpers';

type DuctedSystemMapProps = {
  condenser: CondenserType;
  system: ValidSystemType;
  showTags?: boolean;
  showPrices?: boolean;
};

export default function DuctlessSystemMap({
  condenser,
  system,
  showTags = false,
  showPrices = false
}: DuctedSystemMapProps) {
  const { condenserProducts, airHandlerProducts } = useAppSelector(
    (state) => state.data
  );
  const [condenserInfo, setCondenserInfo] =
    useState<CondenserProductType | null>(null);
  const [zoneInfo, setZoneInfo] = useState<AirHandlerProductType[]>([]);

  useEffect(() => {
    if (system) {
      const condenserProduct = condenserProducts.find(
        (p) => p.productId === system.condenserProductId
      );
      const newZoneInfo: AirHandlerProductType[] = [];
      [
        system.airHandler1ProductId,
        system.airHandler2ProductId,
        system.airHandler3ProductId,
        system.airHandler4ProductId,
        system.airHandler5ProductId,
        system.airHandler6ProductId,
        system.airHandler7ProductId,
        system.airHandler8ProductId,
        system.airHandler9ProductId,
        system.airHandler10ProductId
      ].forEach((id) => {
        const airHandlerProduct = airHandlerProducts.find(
          (p) => p.productId === id
        );
        if (airHandlerProduct) {
          newZoneInfo.push(airHandlerProduct);
        }
      });
      setCondenserInfo(condenserProduct ?? null);
      setZoneInfo(newZoneInfo);
    }
  }, [system]);

  return (
    <div key={condenser.uniqueId} className='flex flex-col'>
      <div className='flex'>
        <div className='flex flex-col pr-2 pt-2'>
          <div className='h-3 w-3 rounded-full bg-gray-300' />
          <div className='ml-[4px] grow border-l-4 border-gray-300' />
        </div>
        <div className='flex grow flex-col gap-2'>
          <div className='flex flex-wrap items-center gap-2'>
            <CpuChipIcon className='h-6 w-6 text-gray-800' />
            <span className='text-xl font-semibold'>{condenser.name}</span>
          </div>
          <div className='flex grow items-start gap-3'>
            <ProductPhoto size='medium' product={condenserInfo ?? undefined} />
            <div className='flex grow flex-col gap-1'>
              <div className='flex w-full items-center'>
                <div className='flex grow flex-wrap items-center gap-2'>
                  <span className='text-lg font-semibold'>
                    {condenserInfo?.name ?? 'Unknown Product'}
                  </span>
                  {showTags && (
                    <>
                      <Tag
                        text={`${abbreviateNumber(
                          Math.round((condenserInfo?.coolingBTU ?? 0) / 1000) *
                            1000
                        )}`}
                      />
                      <Tag text={`${system.seer} SEER`} />
                    </>
                  )}
                </div>
                {showPrices && condenserInfo && (
                  <span className='text-right text-lg font-bold'>
                    {formatUSD(condenserInfo.price, true)}
                  </span>
                )}
              </div>
              {condenserInfo && (
                <ProductLink type='condenser' product={condenserInfo} />
              )}
            </div>
          </div>
        </div>
      </div>
      {condenser.zones.map((zone, zoneIndex) => {
        const product = zoneInfo[zoneIndex];
        return product ? (
          <div key={product.productId} className='flex items-start gap-2'>
            <div className='relative flex items-start self-stretch'>
              {zoneIndex < zoneInfo.length - 1 && (
                <div className='absolute left-[4px] h-full border-l-4 border-gray-300' />
              )}
              <div className='mb-[4px] ml-[4px] w-7 rounded-bl-2xl border-b-4 border-l-4 border-gray-300 pt-10' />
              <div className='mt-[36px] h-3 w-3 rounded-full bg-gray-300' />
            </div>
            <div className='flex grow flex-col gap-2 pt-7'>
              <div className='flex flex-wrap items-center gap-2'>
                <RectangleStackIcon className='h-6 w-6 shrink-0 text-gray-800' />
                <span className='shrink-0 text-xl font-semibold'>
                  {zone.name}
                </span>
              </div>
              <div className='flex grow items-start gap-3'>
                <ProductPhoto size='medium' product={product} />
                <div className='flex grow flex-col gap-1'>
                  <div className='flex w-full items-center'>
                    <div className='flex grow flex-wrap items-center gap-2'>
                      <span className='text-lg font-semibold'>
                        {product.type}
                      </span>
                    </div>
                    {showPrices && (
                      <span className='text-right text-lg font-bold'>
                        {formatUSD(product.price, true)}
                      </span>
                    )}
                  </div>
                  <ProductLink type='air handler' product={product} />
                </div>
              </div>
            </div>
          </div>
        ) : null;
      })}
    </div>
  );
}
